import { useCallback, useMemo, useState } from 'react';
import { es } from 'date-fns/locale';
import { addMonths, format } from 'date-fns';

import { Plan, PlanFrequency, Service } from '@app-types/service.type';
import {
    SearchUser,
    SearchUserInfo,
} from '@components/search-user/search-user';
import { Select } from '@components/custom-select/select';
import {
    SelectItem,
    SelectItemType,
} from '@components/custom-select/select-item';
import DatePicker from '@components/date-picker/date-picker.component';
import BasicButton from '@components/basic-button/basic-button.component';
import NumberUtil, { Currency as UtilCurrency } from '@utils/number.util';
import { Gateway } from '@app-types/payment.type';
import { PurchaseSubscription } from '@app-types/subscription.type';
import { Coupon } from '@app-types/coupon.type';

import { AddPayments, NewPayment } from '../add-payments/add-payments';
import { CouponsModal } from '../coupons-modal/coupons-modal';
import { SelectedCoupon } from '../../components/selected-coupon/selected-coupon';

import styles from './purchase-form.module.scss';

type Props = {
    users: SearchUserInfo[];
    services: Service[];
    gateways: Gateway[];
    searchedCoupons: Coupon[];
    rates: Record<string, number>;
    frequencies: PlanFrequency[];
    onSearchUsers: (query: string) => void;
    onSearchCoupon: (query: string) => void;
    onSubmit: (data: PurchaseSubscription) => Promise<boolean>;
};

type NewInvoiceItem = {
    itemableId: number;
    itemableType: string;
    quantity: number;
    description?: string;
};

const PurchaseForm = ({
    users,
    services,
    gateways,
    rates,
    frequencies,
    searchedCoupons,
    onSearchUsers,
    onSearchCoupon,
    onSubmit,
}: Props) => {
    const [userId, setUserId] = useState<number>();
    const [selectedService, setSelectedService] = useState<Service>();
    const [selectedPlan, setSelectedPlan] = useState<Plan>();
    const [dateFrom, setDateFrom] = useState<Date>();
    const [invoiceItems, setInvoiceItems] = useState<NewInvoiceItem[]>([]);
    const [payments, setPayments] = useState<NewPayment[]>([]);
    const [loading, setLoading] = useState(false);
    const [showCouponModal, setShowCouponModal] = useState(false);
    const [selectedCoupon, setSelectedCoupon] = useState<Coupon>();

    const dateTo = useMemo(() => {
        if (!dateFrom || !selectedPlan) return undefined;
        const frequency = frequencies.find(
            (f) => f.id === selectedPlan.frequencyId
        );
        if (!frequency) return;
        return addMonths(dateFrom, frequency.value);
    }, [dateFrom, selectedPlan, frequencies]);

    const activeServices: SelectItemType[] = useMemo(() => {
        const list: SelectItemType[] = [];
        for (const service of services) {
            const hasActivePlans = service.plans?.some((plan) => plan.active);
            if (hasActivePlans)
                list.push({
                    id: service.id,
                    label: service.name,
                    supportingText: service.code,
                });
        }
        return list;
    }, [services]);

    const activePlans: SelectItemType[] = useMemo(() => {
        if (!selectedService) return [];
        const plans = selectedService.plans?.filter((plan) => plan.active);
        if (!plans) return [];
        return plans.map((plan) => {
            const pricing = plan.prices[0];
            const price = NumberUtil.formatCurrency(
                pricing.price,
                pricing.currency as UtilCurrency
            );
            return {
                id: plan.id,
                label: plan.description,
                supportingText: price,
            };
        });
    }, [selectedService]);

    const coupons = useMemo(() => {
        return searchedCoupons.map((coupon) => ({
            id: coupon.id,
            code: coupon.code,
            discount: coupon.discount,
        }));
    }, [searchedCoupons]);

    const handleUserSearch = (query: string) => {
        if (!query) setUserId(undefined);
        return onSearchUsers(query);
    };

    const selectService = (item: SelectItemType) => {
        const service = services.find((service) => service.id === item.id);
        service && setSelectedService(service);
    };

    const selectPlan = (item: SelectItemType) => {
        if (!selectedService) return;
        const plan = selectedService.plans?.find((p) => p.id === item.id);
        if (!plan) return;
        setSelectedPlan(plan);
        setInvoiceItems((prev) => [
            ...prev,
            {
                itemableId: plan.id,
                itemableType: 'plans',
                quantity: 1,
                description: plan.description,
            },
        ]);
    };

    const addNewPayment = (payment: NewPayment) => {
        setPayments((prev) => [...prev, payment]);
    };

    const removePayment = useCallback(
        (uuid: string) => {
            setPayments((prev) => prev.filter((p) => p.uuid !== uuid));
        },
        [setPayments]
    );

    const selectCoupon = (couponId: number) => {
        const coupon = searchedCoupons.find((coupon) => coupon.id === couponId);
        if (!coupon) return;
        setShowCouponModal(false);
        setSelectedCoupon(coupon);
    };

    const submitPurchase = () => {
        if (!selectedPlan || !userId) return;
        const purchasePayments = payments.map((p) => ({
            amount: p.amount,
            gatewayId: p.gatewayId,
            currency: p.currency,
            date: format(p.date, 'yyyy-MM-dd'),
        }));
        setLoading(true);
        onSubmit({
            userId,
            planId: selectedPlan.id,
            from: format(dateFrom!, 'yyyy-MM-dd'),
            invoice: {
                items: invoiceItems,
                payments: purchasePayments,
            },
        });
    };

    return (
        <section className={styles.purchaseEditor}>
            <form>
                <h4>Cliente</h4>
                <div style={{ marginBottom: 20, width: '100%' }}>
                    <SearchUser
                        users={users}
                        onSearch={handleUserSearch}
                        onSelect={setUserId}
                    />
                </div>
                <h4>Datos de la suscripción</h4>
                <label>Servicio ofrecido</label>
                <Select
                    placeholder="Seleccionar"
                    items={activeServices}
                    onSelect={selectService}
                >
                    {activeServices.map((service) => (
                        <SelectItem
                            key={service.id}
                            id={service.id}
                            supportingText={service.supportingText}
                        >
                            {service.label}
                        </SelectItem>
                    ))}
                </Select>
                {activePlans.length > 0 && (
                    <div style={{ marginTop: 12, width: '100%' }}>
                        <label>Modalidad</label>
                        <Select
                            placeholder="Selecciona un plan"
                            items={activePlans}
                            onSelect={selectPlan}
                        >
                            {activePlans.map((plan) => (
                                <SelectItem
                                    key={plan.id}
                                    id={plan.id}
                                    supportingText={plan.supportingText}
                                >
                                    {plan.label}
                                </SelectItem>
                            ))}
                        </Select>
                    </div>
                )}
                <h4>Facturar a partir de</h4>
                <div className={styles.dateRangeContainer}>
                    <DatePicker onDate={setDateFrom} />
                    {dateTo && (
                        <span>
                            Hasta{' '}
                            {format(dateTo, 'dd MMMM, yyyy', { locale: es })}
                        </span>
                    )}
                </div>
                <div className={styles.couponRow}>
                    {selectedCoupon ? (
                        <SelectedCoupon
                            coupon={selectedCoupon}
                            onRemove={() => setSelectedCoupon(undefined)}
                        />
                    ) : (
                        <span
                            className={styles.link}
                            onClick={() => setShowCouponModal(true)}
                        >
                            Añadir cupón
                        </span>
                    )}
                </div>
                <h4>Pago</h4>
                <AddPayments
                    gateways={gateways}
                    payments={payments}
                    rates={rates}
                    onAddPayment={addNewPayment}
                    onRemovePayment={removePayment}
                />
            </form>
            <footer>
                <BasicButton onClick={submitPurchase} loading={loading}>
                    Crear suscripción
                </BasicButton>
            </footer>
            {showCouponModal && (
                <CouponsModal
                    coupons={coupons}
                    onSearchCoupon={onSearchCoupon}
                    onConfirm={selectCoupon}
                    onDiscard={() => setShowCouponModal(false)}
                />
            )}
        </section>
    );
};

export { PurchaseForm };
